// PublicRoundRobinBooking — the lead-facing page for the Meta self-book
// round-robin calendar. Serves BOTH link shapes that share the public path
// `/schedule/rr/:token` (verified: rotationBookingInvite.js:49 mints
// `<base>/schedule/rr/<bookingToken>` — no `/book/` segment; the campaign link
// is the same shape). On mount the page disambiguates: it tries the per-lead
// slots call first and, only if that 404s, falls back to treating the token as
// a campaign token (availability preview, no direct booking — see note below).
//
// Visual language (radial-gradient dark theme, Card/Field/inputStyle/gradient
// buttons, day-grouped slot grid) is intentionally copied from PublicBooking.jsx
// so this page stays self-contained and PR-isolated. All endpoints are public —
// raw fetch, no auth header. Times render in the lead's local timezone.

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Clock, CheckCircle2, ChevronLeft } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

// CIVIX operates in 49 states — NY is excluded from self-book.
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL',
  'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT',
  'NE', 'NV', 'NH', 'NJ', 'NM', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

export default function PublicRoundRobinBooking() {
  const { token } = useParams();

  // mode: 'loading' | 'perlead' | 'campaign' | 'invalid'
  const [mode, setMode] = useState('loading');
  const [slots, setSlots] = useState([]);
  const [notice, setNotice] = useState(null); // friendly non-error message (e.g. house fallback)
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [confirmed, setConfirmed] = useState(null); // { at, agentName }
  const [cancelled, setCancelled] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);

  // Campaign-branch state.
  const [state, setState] = useState('');
  const [campaignLoading, setCampaignLoading] = useState(false);

  // Disambiguate the token on mount: per-lead first, campaign only on 404.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/public/schedule/rr/book/${encodeURIComponent(token)}/slots?tz=${encodeURIComponent(TZ)}`);
        const data = await res.json().catch(() => ({}));
        if (!alive) return;
        if (res.ok && data.ok) {
          applySlotPayload(data);
          setMode('perlead');
          return;
        }
        if (res.status === 404) { setMode('campaign'); return; }
        // Any other non-ok: surface as invalid rather than guessing.
        setMode('invalid');
      } catch {
        if (alive) setMode('invalid');
      }
    })();
    return () => { alive = false; };
  }, [token]);

  function applySlotPayload(data) {
    setSlots(Array.isArray(data.slots) ? data.slots : []);
    if (data.reason === 'no_agent_for_state' || data.houseFallback) {
      setNotice("We'll reach out to confirm your appointment.");
    } else {
      setNotice(null);
    }
  }

  const slotsByDay = useMemo(() => {
    const map = {};
    for (const s of slots) {
      const key = new Date(s).toISOString().slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(s);
    }
    return map;
  }, [slots]);

  async function refetchPerLeadSlots() {
    const res = await fetch(`${API_BASE}/public/schedule/rr/book/${encodeURIComponent(token)}/slots?tz=${encodeURIComponent(TZ)}`);
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.ok) applySlotPayload(data);
  }

  async function book() {
    if (!selected) return;
    setActionError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/public/schedule/rr/book/${encodeURIComponent(token)}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ startAt: selected, tz: TZ }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 201 || (res.status === 200 && data.idempotent)) {
        setConfirmed({ at: data.appointment?.startAt || selected, agentName: data.agent?.name || null });
        return;
      }
      if (res.status === 409 && data.error === 'slot_unavailable') {
        setActionError('That time was just taken — please pick another.');
        setSelected(null);
        await refetchPerLeadSlots();
        return;
      }
      if (res.status === 422) { setNotice("We'll reach out to confirm your appointment."); setSelected(null); return; }
      setActionError(friendlyError(data.error));
    } catch {
      setActionError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function startReschedule() {
    setActionError(null);
    const res = await fetch(`${API_BASE}/public/schedule/rr/book/${encodeURIComponent(token)}/reschedule-slots?tz=${encodeURIComponent(TZ)}`);
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.ok) {
      setSlots(Array.isArray(data.slots) ? data.slots : []);
      setSelected(null);
      setConfirmed(null);
      setRescheduling(true);
    } else {
      setActionError('Could not load times to reschedule. Please reply to our text.');
    }
  }

  async function reschedule() {
    if (!selected) return;
    setActionError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/public/schedule/rr/book/${encodeURIComponent(token)}/reschedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ startAt: selected, tz: TZ }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setConfirmed({ at: data.appointment?.startAt || selected, agentName: data.agent?.name || null });
        setRescheduling(false);
        return;
      }
      if (res.status === 409) {
        setActionError('That time was just taken — please pick another.');
        setSelected(null);
        return;
      }
      setActionError(friendlyError(data.error));
    } catch {
      setActionError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function cancel() {
    if (!window.confirm('Cancel this appointment?')) return;
    setActionError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/public/schedule/rr/book/${encodeURIComponent(token)}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tz: TZ }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) { setCancelled(true); setConfirmed(null); }
      else setActionError('Could not cancel. Please reply to our text and we\'ll help.');
    } catch {
      setActionError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function loadCampaignSlots(st) {
    setState(st);
    setCampaignLoading(true);
    setSelected(null);
    try {
      const res = await fetch(`${API_BASE}/public/schedule/rr/${encodeURIComponent(token)}/slots?state=${encodeURIComponent(st)}&tz=${encodeURIComponent(TZ)}`);
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setSlots(Array.isArray(data.slots) ? data.slots : []);
        setNotice(data.reason === 'no_agent_for_state' || data.houseFallback
          ? "No agents are available in your state right now — we'll reach out to confirm."
          : null);
      } else if (res.status === 404) {
        setMode('invalid');
      }
    } finally {
      setCampaignLoading(false);
    }
  }

  return (
    <Shell>
      {mode === 'loading' && <Card>Loading availability…</Card>}

      {mode === 'invalid' && (
        <Card><div style={{ color: '#fca5a5' }}>This booking link is invalid or has expired.</div></Card>
      )}

      {cancelled && (
        <Card>
          <Centered icon={<CheckCircle2 size={48} color="#22d3ee" />} title="Appointment cancelled">
            Your appointment is cancelled. Reply to our text and we'll help you rebook.
          </Centered>
        </Card>
      )}

      {confirmed && !cancelled && (
        <Card>
          <Centered icon={<CheckCircle2 size={48} color="#22d3ee" />} title="You're booked">
            <div>{formatFull(confirmed.at)}</div>
            {confirmed.agentName && (
              <div style={{ marginTop: 8 }}>Booked with {confirmed.agentName}</div>
            )}
            <div style={{ marginTop: 12, fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>
              We'll send you a confirmation text shortly.
            </div>
          </Centered>
          {mode === 'perlead' && (
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 16 }}>
              <SecondaryButton onClick={startReschedule} disabled={submitting}>Reschedule</SecondaryButton>
              <SecondaryButton onClick={cancel} disabled={submitting} danger>Cancel</SecondaryButton>
            </div>
          )}
          {actionError && <ErrorBox>{actionError}</ErrorBox>}
        </Card>
      )}

      {/* Campaign fallback: state selector before slots. */}
      {mode === 'campaign' && !confirmed && !cancelled && !state && (
        <Card>
          <SectionTitle icon={<Calendar size={18} color="#22d3ee" />}>Where are you located?</SectionTitle>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, margin: '4px 0 12px' }}>
            Select your state to see available times.
          </p>
          <Field label="State">
            <select value={state} onChange={(e) => e.target.value && loadCampaignSlots(e.target.value)} style={inputStyle}>
              <option value="">Choose your state…</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </Card>
      )}

      {/* Slot grid — shared by per-lead, reschedule, and campaign-preview. */}
      {!confirmed && !cancelled && !selected && (mode === 'perlead' || (mode === 'campaign' && state)) && (
        <>
          {notice && <Card><div style={{ color: 'rgba(255,255,255,0.75)' }}>{notice}</div></Card>}
          {mode === 'campaign' && (
            <Card><div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
              Showing availability for {state}. We'll text you a personal link to lock in your time.
            </div></Card>
          )}
          <Card>
            <SectionTitle icon={<Calendar size={18} color="#22d3ee" />}>
              {rescheduling ? 'Pick a new time' : 'Pick a time'}
            </SectionTitle>
            {campaignLoading && <div style={{ color: 'rgba(255,255,255,0.5)', padding: 8 }}>Loading availability…</div>}
            {!campaignLoading && Object.keys(slotsByDay).length === 0 && (
              <div style={{ color: 'rgba(255,255,255,0.5)', padding: 16, textAlign: 'center' }}>
                No times available right now. Please check back later.
              </div>
            )}
            {Object.entries(slotsByDay).slice(0, 7).map(([day, daySlots]) => (
              <div key={day} style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 600, marginBottom: 8, color: 'rgba(255,255,255,0.85)' }}>
                  {new Date(day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 }}>
                  {daySlots.map((s) => (
                    <button key={s} onClick={() => mode === 'perlead' ? setSelected(s) : null} disabled={mode === 'campaign'}
                      style={slotButtonStyle(mode === 'campaign')}
                      onMouseOver={(e) => { if (mode === 'perlead') e.currentTarget.style.background = 'rgba(139, 92, 246, 0.3)'; }}
                      onMouseOut={(e) => { if (mode === 'perlead') e.currentTarget.style.background = 'rgba(139, 92, 246, 0.12)'; }}
                    >
                      {new Date(s).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {actionError && <ErrorBox>{actionError}</ErrorBox>}
          </Card>
        </>
      )}

      {/* Per-lead confirm — NO PII, identity is on the token. */}
      {mode === 'perlead' && selected && !confirmed && !cancelled && (
        <Card>
          <BackButton onClick={() => setSelected(null)} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
            <Clock size={18} color="#22d3ee" />{formatFull(selected)}
          </div>
          {actionError && <ErrorBox>{actionError}</ErrorBox>}
          <PrimaryButton onClick={rescheduling ? reschedule : book} disabled={submitting}>
            {submitting ? 'Booking…' : (rescheduling ? 'Confirm new time' : 'Confirm booking')}
          </PrimaryButton>
        </Card>
      )}
    </Shell>
  );
}

// ─── Local presentational helpers (copied from PublicBooking.jsx style) ──────
function Shell({ children }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(circle at 20% 0%, #1a0a3a 0%, #0a0a1f 60%)',
      color: 'white', fontFamily: 'Inter, system-ui, sans-serif', padding: '24px 16px',
    }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ marginBottom: 32 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'linear-gradient(90deg, #8b5cf6, #22d3ee)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            fontWeight: 700, fontSize: 14, letterSpacing: 1, textTransform: 'uppercase',
          }}>CIVIX</div>
          <h1 style={{ fontSize: 32, fontWeight: 700, margin: '8px 0 4px' }}>Book your appointment</h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 15 }}>Pick a time and we'll confirm by text.</p>
        </div>
        {children}
        <div style={{ textAlign: 'center', marginTop: 32, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
          Powered by CIVIX
        </div>
      </div>
    </div>
  );
}

function Card({ children }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 16, padding: 24, backdropFilter: 'blur(10px)', marginBottom: 16,
    }}>{children}</div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{label}</span>
      {children}
    </label>
  );
}

function SectionTitle({ icon, children }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, marginBottom: 16 }}>{icon} {children}</div>;
}

function Centered({ icon, title, children }) {
  return (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      <div style={{ margin: '0 auto 16px', display: 'inline-block' }}>{icon}</div>
      <h2 style={{ fontSize: 22, fontWeight: 600, marginBottom: 8 }}>{title}</h2>
      <div style={{ color: 'rgba(255,255,255,0.7)' }}>{children}</div>
    </div>
  );
}

function BackButton({ onClick }) {
  return (
    <button onClick={onClick} style={{
      background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)',
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
      marginBottom: 16, padding: 0, fontSize: 14,
    }}><ChevronLeft size={16} /> Pick a different time</button>
  );
}

function PrimaryButton({ children, ...props }) {
  return (
    <button {...props} style={{
      background: 'linear-gradient(90deg, #8b5cf6, #22d3ee)', color: 'white', border: 'none',
      padding: '12px 16px', borderRadius: 8, fontWeight: 600, fontSize: 15, cursor: 'pointer',
      opacity: props.disabled ? 0.6 : 1, marginTop: 8, width: '100%',
    }}>{children}</button>
  );
}

function SecondaryButton({ children, danger, ...props }) {
  return (
    <button {...props} style={{
      background: 'transparent', color: danger ? '#fca5a5' : 'white',
      border: `1px solid ${danger ? 'rgba(239,68,68,0.5)' : 'rgba(139,92,246,0.5)'}`,
      padding: '10px 16px', borderRadius: 8, fontWeight: 600, fontSize: 14,
      cursor: 'pointer', opacity: props.disabled ? 0.6 : 1,
    }}>{children}</button>
  );
}

function ErrorBox({ children }) {
  return (
    <div style={{
      background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)',
      color: '#fca5a5', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginTop: 12,
    }}>{children}</div>
  );
}

const inputStyle = {
  background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8, color: 'white', padding: '10px 12px',
  fontSize: 15, outline: 'none', fontFamily: 'inherit',
};

function slotButtonStyle(disabled) {
  return {
    background: 'rgba(139, 92, 246, 0.12)', border: '1px solid rgba(139, 92, 246, 0.4)',
    color: 'white', padding: '10px 12px', borderRadius: 8,
    cursor: disabled ? 'default' : 'pointer', fontSize: 14, transition: 'all 0.15s',
    opacity: disabled ? 0.7 : 1,
  };
}

function formatFull(iso) {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function friendlyError(code) {
  switch (code) {
    case 'slot_in_past': return 'That time has already passed — please pick another.';
    case 'invalid_startAt':
    case 'startAt_required': return 'Please choose a valid time.';
    case 'no_agent_for_state': return "We'll reach out to confirm your appointment.";
    default: return 'Something went wrong. Please try again.';
  }
}
